import { SeasonFragment, SeasonLeagueFragment } from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { useTranslate } from '@tolgee/react';
import { useEffect, useMemo, useState } from 'react';
import { MatchFilter } from '@amf/shared/data/hooks/useMatches';
import useTeamList from '@amf/shared/data/hooks/useTeamList';
import { LeaguePhaseType } from '@amf/shared/data/League';
import SeasonHeader from '@amf/shared/components/season/SeasonHeader';
import Menu from '@amf/shared/components/menu/Menu';
import Container from '@amf/shared/components/layout/Container';
import LeagueMatchesFilter from '@amf/shared/components/league/LeagueMatchesFilter';
import LeagueMatches from '@amf/shared/components/league/LeagueMatches';
import Empty from '@amf/shared/components/utils/Empty';
import usePersistentState from '@amf/shared/hooks/usePersistentState';

export interface MatchListPageProps extends NavigationProps {
  seasons: SeasonFragment[];
}

type Props = {
  seasons: SeasonFragment[];
  links: { match: string; team: string };
  className?: string;
};

function MatchListPage({ seasons, links, className }: Props) {
  const { t } = useTranslate();
  const [season, setSeason] = usePersistentState<SeasonFragment>(seasons, 'season');
  const [league, setLeague] = usePersistentState<SeasonLeagueFragment | undefined>(
    season?.leagues ?? [],
    'league',
  );

  const [filter, setFilter] = useState<MatchFilter | undefined>({
    sortBy: 'round',
  });
  const { teams } = useTeamList(league?.id);

  useEffect(() => {
    if (league?.phases.length === 0) return;
    const phase = league?.phases.find(phase => phase.current) ?? league?.phases[0];
    setFilter({ phase: phase?.id, sortBy: 'round' });
  }, [league]);

  const type = useMemo(() => {
    if (!filter || !league) return null;
    return league.phases.find(p => p.id === filter.phase)?.type as LeaguePhaseType | undefined;
  }, [filter, league]);

  if (!season) return null;
  return (
    <>
      <SeasonHeader
        className={className}
        title={t('match.list.title')}
        seasons={seasons}
        selected={season}
        onSeasonChange={season => {
          setSeason(season);
          setLeague(season.leagues[0]);
        }}
      />
      {league ? (
        <>
          <Menu<SeasonLeagueFragment>
            scrollable
            label={t('seasonLeague.label')}
            onChange={({ value }) => setLeague(value)}
            selected={{ id: league.id, title: league.name, value: league }}
            items={season.leagues.map(league => ({
              id: league.id,
              title: league.name,
              value: league,
            }))}
          />
          {league.phases.length > 0 ? (
            <Container>
              <LeagueMatchesFilter
                league={league}
                teams={teams}
                onChange={data =>
                  setFilter({
                    team: data.team.length > 0 ? data.team : null,
                    phase: data.phase.length > 0 ? data.phase : null,
                    dateFrom: data.dateFrom,
                    dateTo: data.dateTo,
                    round: data.round.length > 0 ? Number(data.round) : undefined,
                    sortBy: data.sortBy ?? 'round',
                  })
                }
              />
              <LeagueMatches
                filter={filter}
                season={season}
                league={league}
                type={type}
                prefixes={{ match: links.match, team: links.team }}
              />
            </Container>
          ) : (
            <Container>
              <Empty>{t('match.list.noPhases')}</Empty>
            </Container>
          )}
        </>
      ) : (
        <Container>
          <Empty>{t('match.list.noLeagues')}</Empty>
        </Container>
      )}
    </>
  );
}

export default MatchListPage;

export const getServerSideProps =
  (): GetServerSideProps<MatchListPageProps> =>
  async ({ locale, res }) => {
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=10');
    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    if (seasons.length === 0) return { notFound: true };

    return {
      props: {
        seasons,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
